import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Home as HomeIcon,
  MapPin,
  CalendarCheck,
  ClipboardList,
  Sparkles,
  ArrowRight,
  Quote,
} from "lucide-react";
import FadeUp from "../components/site/FadeUp";
import CTASection from "../components/site/CTASection";
import { SERVICES, TOWNS, TESTIMONIALS, BRAND } from "../data/site";
import InstagramSlideshow from "../components/site/InstagramSlideshow";
import founderPhoto from "../assets/founder.jpg";

const TRUST = [
  { icon: ShieldCheck, label: "Fully Insured & Bonded" },
  { icon: Lock, label: "Confidentiality Agreement" },
  { icon: HomeIcon, label: "Locally Owned" },
  { icon: MapPin, label: "Serving the South Shore" },
];

const STEPS = [
  {
    icon: CalendarCheck,
    title: "Book a walkthrough",
    body: "We come to your home, listen, and get to know how your household actually runs — free, no pressure.",
  },
  {
    icon: ClipboardList,
    title: "We build your custom plan",
    body: "You get a plan made for your home and your week: the tasks that matter most, handled the way you like.",
  },
  {
    icon: Sparkles,
    title: "Your team comes weekly",
    body: "The same trusted team returns each week to reset your home, so you come back to calm — and your time.",
  },
];

const moreWays = SERVICES.filter((s) => s.slug !== "weekly-home-concierge");
const weekly = SERVICES.find((s) => s.slug === "weekly-home-concierge");

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="section-pad pb-10 text-center">
        <div className="container-hh flex flex-col items-center">
          <FadeUp>
            <img
              src={BRAND.logo}
              alt="Harbor Home Concierge Co. — Weekly support, organization, move services, rental turnovers, restock"
              className="mx-auto w-full max-w-xl lg:max-w-2xl"
            />
          </FadeUp>
          <FadeUp delay={0.1} className="mt-6 max-w-3xl">
            <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              We want to give you your time back.
            </h1>
            <p className="mt-5 font-serif text-2xl leading-snug text-teal md:text-3xl">
              Personalized home management for busy families.
            </p>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-bodytext">
              Harbor Home provides personalized home management for busy South
              Shore families. From laundry and kitchen resets to organization,
              errands, and the everyday tasks that keep a household running, we
              help you reclaim your time.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/book" data-testid="hero-book-btn" className="btn-teal">
                Book Your Free Walkthrough
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-how-btn"
                className="btn-outline-navy"
              >
                See how it works
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Instagram slideshow (replaces stock hero banner) */}
      <section data-testid="instagram-section" className="px-6 pb-10 md:px-10 md:pb-16 lg:px-16">
        <FadeUp className="container-hh">
          <p className="eyebrow mb-6 text-center">Straight from our Instagram</p>
          <InstagramSlideshow />
        </FadeUp>
      </section>

      {/* Founder bio */}
      <section data-testid="founder-section" className="section-pad pt-4">
        <div className="container-hh grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <div className="overflow-hidden rounded-[12px] shadow-cozy-lg">
              <img
                src={founderPhoto}
                alt="Sarah, founder of Harbor Home Concierge, with her daughter by the beach"
                className="h-full max-h-[520px] w-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="eyebrow">Meet our founder</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">Hi, I'm Sarah.</h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-bodytext">
              <p>
                After years as a college soccer coach, I found myself juggling
                work, motherhood, and the endless mental load that so many women
                quietly carry. I realized there was a gap — not for another
                cleaning company, but for someone to truly help families breathe
                again.
              </p>
              <p>
                That's why I created Harbor Home: to give busy families more
                time, less stress, and the freedom to be fully present for the
                moments that matter most.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="founder-readmore"
              className="btn-outline-navy mt-7"
            >
              Read our story
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* Trust band */}
      <section data-testid="trust-band" className="bg-seafoam px-6 py-8 md:px-10 lg:px-16">
        <div className="container-hh grid grid-cols-2 gap-6 md:grid-cols-4">
          {TRUST.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <Icon className="h-6 w-6 flex-none text-teal" />
              <span className="text-sm font-semibold text-navy">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="section-pad">
        <div className="container-hh">
          <FadeUp className="max-w-2xl">
            <p className="eyebrow">How it works</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
              Three simple steps to a calmer home
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.1}>
                <div className="cozy-card h-full p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-seafoam">
                    <s.icon className="h-6 w-6 text-teal" />
                  </div>
                  <p className="mt-5 text-sm font-semibold text-teal">
                    Step {i + 1}
                  </p>
                  <h3 className="mt-1 text-2xl">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-bodytext">{s.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Hero service card — Weekly Home Management */}
      <section className="section-pad bg-seafoam">
        <div className="container-hh">
          <FadeUp>
            <div className="grid overflow-hidden rounded-[12px] bg-white shadow-cozy-lg lg:grid-cols-2">
              <div className="order-2 p-8 md:p-12 lg:order-1">
                <p className="eyebrow">Our signature service</p>
                <h2 className="mt-3 text-3xl md:text-4xl">Weekly Home Management</h2>
                <p className="mt-4 leading-relaxed text-bodytext">
                  A standing weekly relationship with a team that knows your
                  home. Laundry, kitchen resets, tidying, organizing, errands,
                  restocking, and the small projects on your list — handled.
                </p>
                <ul className="mt-6 space-y-2 text-bodytext">
                  {[
                    "2-hour minimum",
                    "Weekly service",
                    "3-month commitment",
                    "Billed monthly",
                  ].map((p) => (
                    <li key={p} className="flex items-center gap-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-teal" />
                      <span className="font-medium text-navy">{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/book" data-testid="weekly-book-btn" className="btn-teal">
                    Book Your Free Walkthrough
                  </Link>
                  <Link
                    to="/services/weekly-home-concierge"
                    data-testid="weekly-learn-btn"
                    className="btn-outline-navy"
                  >
                    See the full task list
                  </Link>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src={weekly.image}
                  alt="Weekly Home Management — a bright, reset kitchen"
                  loading="lazy"
                  className="h-64 w-full object-cover lg:h-full"
                />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* More ways we help */}
      <section className="section-pad">
        <div className="container-hh">
          <FadeUp className="max-w-2xl">
            <p className="eyebrow">More ways we help</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
              Project services for life's bigger moments
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {moreWays.map((s, i) => (
              <FadeUp key={s.slug} delay={(i % 3) * 0.1}>
                <Link
                  to={s.path}
                  data-testid={`more-ways-${s.slug}`}
                  className="cozy-card group flex h-full flex-col overflow-hidden"
                >
                  <div className="overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="text-2xl">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-bodytext">
                      {s.blurb}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-teal">
                      {s.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-pad bg-seafoam">
        <div className="container-hh">
          <FadeUp className="max-w-2xl">
            <p className="eyebrow">What families say</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
              Trusted in homes across the South Shore
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={t.name} delay={(i % 2) * 0.1}>
                <figure className="cozy-card flex h-full flex-col p-8">
                  <Quote className="h-8 w-8 text-seafoamlight" />
                  <blockquote className="mt-4 flex-1 text-lg leading-relaxed text-bodytext">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 font-serif text-xl text-navy">
                    {t.name} · {t.town}
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Town list band */}
      <section data-testid="town-band" className="section-pad">
        <div className="container-hh text-center">
          <FadeUp>
            <p className="eyebrow">Proudly serving</p>
            <h2 className="mt-3 text-3xl md:text-4xl">The South Shore, Massachusetts</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {TOWNS.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-seafoam px-5 py-2 text-sm font-medium text-navy"
                >
                  {t}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Careers */}
      <section data-testid="careers-section" className="section-pad bg-seafoam">
        <FadeUp className="container-hh text-center">
          <p className="eyebrow">Join our team</p>
          <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
            Looking to work with Harbor Home?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-bodytext">
            We're always looking for caring, reliable people who share our
            passion for helping busy families.
          </p>
          <Link
            to="/careers"
            data-testid="careers-apply-btn"
            className="btn-teal mt-8"
          >
            Apply to Join Our Team
          </Link>
        </FadeUp>
      </section>

      <CTASection />
    </div>
  );
}
